import { useCallback } from 'react'
import { atom, useAtom, useAtomValue } from 'jotai'
import { atomFamily } from 'jotai/utils'
import { useQueryClient } from '@tanstack/react-query'
import type { AudioNote } from '@highlight/common'
import { GlobalEvents } from '@highlight/ipc'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'

const log = GlobalLogger.bind('SummaryDraft')

/**
 * Unsaved edits to one note's summary. Null means not editing, so the saved summary shows. Nothing
 * here touches the note until `publishSummaryDraft`, so a public link keeps the saved text meanwhile.
 */
export interface SummaryDraft {
  text: string
  /** The prompt that produced `text`, or null when the text was typed by hand. */
  instruction: string | null
}

export const summaryDraftAtom = atomFamily((_noteId: string) => atom<SummaryDraft | null>(null))

export interface SummaryEditStatus {
  /** A revision is being requested or revealed, or a save is in flight. Locks the editor and its save controls. */
  isBusy: boolean
  /** The last save failed; the draft is kept so it can be saved again. */
  saveError: string | null
}

export const summaryEditStatusAtom = atomFamily((_noteId: string) =>
  atom<SummaryEditStatus>({ isBusy: false, saveError: null }),
)

/** Whether a draft has text the saved summary doesn't. */
export function summaryHasChanges(draft: SummaryDraft | null, savedText: string): boolean {
  return draft !== null && draft.text !== savedText
}

/** Saves a draft as the note's summary. Rejects on failure so the editor can keep the draft to retry. */
export async function publishSummaryDraft(noteId: string, text: string): Promise<void> {
  await AudioNoteIpcRenderer.updateAudioNote({ id: noteId, updates: { summary: text } })
  GlobalEvents.emit('audio-note:summary-ready')
}

/** Whether a note's summary has unsaved edits. Keyed by `''` when there is no note, which holds no draft. */
export function useIsSummaryDirty(noteId: string | null, savedText: string): boolean {
  return summaryHasChanges(useAtomValue(summaryDraftAtom(noteId ?? '')), savedText)
}

/**
 * Save and discard for a note's summary draft. The editor and the floating save controls both call it,
 * and they share the draft and status atoms, so either one can save the other's edits.
 */
export function useSummarySave(noteId: string, savedText: string, isPublished: boolean) {
  const [draft, setDraft] = useAtom(summaryDraftAtom(noteId))
  const [status, setStatus] = useAtom(summaryEditStatusAtom(noteId))
  const queryClient = useQueryClient()

  const save = useCallback(async () => {
    if (!draft || !summaryHasChanges(draft, savedText) || status.isBusy) {
      return
    }
    setStatus((current) => ({ ...current, isBusy: true, saveError: null }))
    try {
      await publishSummaryDraft(noteId, draft.text)
      // Write the saved text into the note's cache before dropping the draft. Otherwise the editor falls
      // back to the old summary until the refetch lands.
      queryClient.setQueryData<AudioNote | null | undefined>(['audio-note', noteId], (note) =>
        note ? { ...note, summary: draft.text } : note,
      )
      setDraft(null)
      setStatus((current) => ({ ...current, isBusy: false }))
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'publish summary',
        edited_with_prompt: draft.instruction !== null,
        public_link: isPublished,
        trigger: 'sidepanel meeting view',
      })
    } catch (publishError) {
      // The draft stays so the edit isn't lost; the user can save again.
      log.error(`Failed to publish summary for audio note ${noteId}: ${String(publishError)}`)
      setStatus((current) => ({
        ...current,
        isBusy: false,
        saveError: 'Could not save the summary. Your edits are still here, so try again.',
      }))
    }
  }, [draft, savedText, status.isBusy, noteId, isPublished, queryClient, setDraft, setStatus])

  const discard = useCallback(() => {
    setDraft(null)
    setStatus((current) => ({ ...current, saveError: null }))
  }, [setDraft, setStatus])

  return {
    hasChanges: summaryHasChanges(draft, savedText),
    isBusy: status.isBusy,
    saveError: status.saveError,
    save,
    discard,
  }
}
